import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Plus, Search, ArrowUpDown } from 'lucide-react';

import Input from '../components/Input';
import Button from '../components/Button';
import Loading from '../components/Loading';
import Select from '../components/Select';
import { addProcess, getProcesses, updateProcess, deleteProcess } from '../actions/processActions';
import {
    ADD_PROCESS_RESET,
    GET_PROCESSES_RESET,
    UPDATE_PROCESS_RESET,
    DELETE_PROCESS_RESET,
} from '../constants/processConstants';

const processTypes = [
    { id: 'Recovery', title: 'Recovery' },
    { id: 'Flow', title: 'Flow' }
];

const ProcessList = () => {
    // States - following UserList structure
    const [data, setData] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('process');
    const [sortDirection, setSortDirection] = useState('asc');
    const [editingProcess, setEditingProcess] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);

    // Form states
    const [process, setProcess] = useState('');
    const [processType, setProcessType] = useState('Recovery');

    const dispatch = useDispatch();

    // Check for auth - following UserList structure
    const userLogin = useSelector((state) => state.userLogin);
    const { userInfo } = userLogin;

    useEffect(() => {
        if (!userInfo) {
            // Redirect to login if needed
        }
    }, [userInfo]);

    useEffect(() => {
        dispatch(getProcesses(currentPage, 10, searchTerm));
    }, [dispatch, searchTerm, currentPage]);

    // Get Processes - following UserList structure exactly
    const getProcessesInfo = useSelector((state) => state.getProcessesInfo);
    const { loadingGetProcesses, errorGetProcesses, getProcessesData } = getProcessesInfo;

    useEffect(() => {
        dispatch({ type: GET_PROCESSES_RESET });
        if (getProcessesData) {
            setLoading(false);
            setData(getProcessesData.data); // Copy to local state like UserList
            setTotalPages(getProcessesData.pages);
        } else if (errorGetProcesses) {
            setLoading(false);
            toast(errorGetProcesses, {
                type: 'error',
                hideProgressBar: true,
                autoClose: 2000,
            });
        }
    }, [getProcessesData, errorGetProcesses]);

    // Add Process - following UserList structure
    const addProcessInfo = useSelector((state) => state.addProcessInfo);
    const { loadingAddProcess, errorAddProcess, addProcessData } = addProcessInfo;

    useEffect(() => {
        dispatch({ type: ADD_PROCESS_RESET });
        if (addProcessData) {
            toast('Process added successfully', {
                type: 'success',
                hideProgressBar: true,
                autoClose: 2000,
            });
            closeModal();
            setTimeout(() => {
                dispatch(getProcesses(currentPage, 10, searchTerm));
            }, 1000);
        } else if (errorAddProcess) {
            toast(errorAddProcess, {
                type: 'error',
                hideProgressBar: true,
                autoClose: 2000,
            });
        }
    }, [addProcessData, errorAddProcess]);

    // Update Process - following UserList structure
    const updateProcessInfo = useSelector((state) => state.updateProcessInfo);
    const { loadingUpdateProcess, errorUpdateProcess, updateProcessData } = updateProcessInfo;

    useEffect(() => {
        dispatch({ type: UPDATE_PROCESS_RESET });
        if (updateProcessData) {
            toast('Process updated successfully', {
                type: 'success',
                hideProgressBar: true,
                autoClose: 2000,
            });
            closeModal();
            setTimeout(() => {
                dispatch(getProcesses(currentPage, 10, searchTerm));
            }, 1000);
        } else if (errorUpdateProcess) {
            toast(errorUpdateProcess, {
                type: 'error',
                hideProgressBar: true,
                autoClose: 2000,
            });
        }
    }, [updateProcessData, errorUpdateProcess]);

    // Delete Process - following UserList structure
    const deleteProcessInfo = useSelector((state) => state.deleteProcessInfo);
    const { loadingDeleteProcess, errorDeleteProcess, deleteProcessData } = deleteProcessInfo;

    useEffect(() => {
        dispatch({ type: DELETE_PROCESS_RESET });
        if (deleteProcessData) {
            toast('Process deleted successfully', {
                type: 'success',
                hideProgressBar: true,
                autoClose: 2000,
            });
            setTimeout(() => {
                dispatch(getProcesses(currentPage, 10, searchTerm));
            }, 1000);
        } else if (errorDeleteProcess) {
            toast(errorDeleteProcess, {
                type: 'error',
                hideProgressBar: true,
                autoClose: 2000,
            });
        }
    }, [deleteProcessData, errorDeleteProcess]);

    // Modal handlers - following UserList structure
    const openModal = () => {
        setEditingProcess(null);
        setProcess('');
        setProcessType('Recovery');
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setProcess('');
        setProcessType('Recovery');
        setEditingProcess(null);
    };

    // Submit handler - following UserList structure
    const handleSubmit = () => {
        if (!process || !processType) {
            toast('All fields are mandatory', {
                type: 'error',
                hideProgressBar: true,
                autoClose: 2000,
            });
            return;
        }

        if (editingProcess) {
            dispatch(updateProcess(editingProcess._id, process));
        } else {
            dispatch(addProcess(process, processType));
        }
    };

    const handleDeleteProcess = (id) => {
        if (window.confirm('Are you sure you want to delete this process?')) {
            dispatch(deleteProcess(id));
        }
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const openEditModal = (processData) => {
        setEditingProcess(processData);
        setProcess(processData.process);
        setProcessType(processData.processType || 'Recovery');
        setShowModal(true);
    };

    const ProcessTypeBadge = ({ type }) => {
        const bgColor = type === 'Recovery' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800';
        return (
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${bgColor}`}>
                {type}
            </span>
        );
    };

    if (loading) {
        return <Loading />;
    }

    // Use local state data like UserList does
    const sortedProcesses = [...data].sort((a, b) => {
        const fieldA = (a[sortField] || '').toLowerCase();
        const fieldB = (b[sortField] || '').toLowerCase();
        if (fieldA < fieldB) return sortDirection === 'asc' ? -1 : 1;
        if (fieldA > fieldB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    });

    return (
        <div className="w-full h-full">
            <h1 className="text-2xl font-semibold">Process Management</h1>
            <div className="bg-white shadow-md rounded px-8 py-4 my-4">
                <div className="flex justify-between">
                    <div className="flex gap-5">
                        <Input
                            name="Search Process"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="flex gap-3">
                        <Button
                            type="button"
                            onClick={openModal}
                            text="Add Process"
                        />
                    </div>
                </div>
            </div>

            <div className="bg-white shadow-md rounded">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                {['process', 'processType'].map((field) => (
                                    <th
                                        key={field}
                                        onClick={() => handleSort(field)}
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                    >
                                        <div className="flex items-center space-x-1">
                                            <span>
                                                {field === 'process'
                                                    ? 'Process Name'
                                                    : field === 'processType'
                                                        ? 'Process Type'
                                                        : field.charAt(0).toUpperCase() + field.slice(1)}
                                            </span>
                                            <ArrowUpDown size={14} className="text-gray-400" />
                                        </div>
                                    </th>
                                ))}
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {sortedProcesses.length > 0 ? (
                                sortedProcesses.map((processItem) => (
                                    <tr key={processItem._id} className="hover:bg-gray-50 transition">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                                                    <span className="text-blue-600 font-medium">
                                                        {processItem.process ? processItem.process.charAt(0).toUpperCase() : '?'}
                                                    </span>
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">{processItem.process}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <ProcessTypeBadge type={processItem.processType || 'N/A'} />
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <button
                                                className="text-blue-600 hover:text-blue-900 mr-3"
                                                onClick={() => openEditModal(processItem)}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                className="text-red-600 hover:text-red-900"
                                                onClick={() => handleDeleteProcess(processItem._id)}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="3" className="px-6 py-12 text-center">
                                        <div className="text-gray-500">No processes found</div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    {sortedProcesses.length > 0 && (
                        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-600">
                            Showing {sortedProcesses.length} of {data.length} processes
                        </div>
                    )}
                </div>
            </div>

            {/* Modal - following UserList structure exactly */}
            {showModal && (
                <>
                    <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
                        <div className="relative w-auto my-6 mx-auto max-w-3xl mt-6">
                            <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                                <div className="flex items-center justify-between py-3 px-3 border-b border-solid border-blueGray-200 rounded-t">
                                    <h3 className="text-lg font-semibold">
                                        {editingProcess ? 'Edit Process' : 'Add Process'}
                                    </h3>
                                </div>
                                <div className="relative p-6 flex-auto">
                                    <Input
                                        width="w-full mb-4"
                                        name="Process Name *"
                                        value={process}
                                        onChange={(e) => setProcess(e.target.value)}
                                    />
                                    <Select
                                        width="w-full mb-4"
                                        name="Process Type *"
                                        value={processType}
                                        onChange={(e) => setProcessType(e.target.value)}
                                        options={processTypes}
                                    />
                                    <p className="text-sm">All the fields with * are mandatory</p>
                                </div>
                                <div className="flex items-center justify-end py-2 px-3 border-t border-solid border-blueGray-200 rounded-b">
                                    <button
                                        className="text-red-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                                        type="button"
                                        onClick={closeModal}
                                    >
                                        Close
                                    </button>
                                    <button
                                        className="bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-6 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 disabled:bg-gray-300"
                                        type="button"
                                        onClick={handleSubmit}
                                        disabled={loadingAddProcess || loadingUpdateProcess}
                                    >
                                        {editingProcess ? 'Update' : 'Add'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
                </>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="flex justify-center items-center space-x-2 mt-4">
                    <button
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className={`px-3 py-1 rounded border ${currentPage === 1 ? 'bg-gray-200 cursor-not-allowed' : 'bg-white hover:bg-gray-100'
                            }`}
                    >
                        Prev
                    </button>

                    {[...Array(totalPages)].map((_, i) => {
                        const page = i + 1;
                        return (
                            <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`px-3 py-1 rounded border ${page === currentPage ? 'bg-blue-600 text-white' : 'bg-white hover:bg-gray-100'
                                    }`}
                            >
                                {page}
                            </button>
                        );
                    })}

                    <button
                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className={`px-3 py-1 rounded border ${currentPage === totalPages ? 'bg-gray-200 cursor-not-allowed' : 'bg-white hover:bg-gray-100'
                            }`}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default ProcessList;